// =============================================================================
// VandieZ shipper-web -- Inbox (/inbox)
// =============================================================================
// The shipper's notification feed — same data the mobile app shows
// under the bell icon. Backed by GET /users/me/notifications. Tap a
// row to follow its `route_path` (when set); also flips read_at.
// =============================================================================

import {
  AlertCircle,
  Bell,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Mail,
  MailOpen,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { inboxApi } from "@/api/client";
import { PageHeader } from "@/components/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { NotificationRead } from "@/types/inbox";

const PAGE_SIZE = 50;
type Filter = "all" | "unread";

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {return iso;}
  const seconds = Math.max(1, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) {return `${seconds}s ago`;}
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {return `${minutes}m ago`;}
  const hours = Math.round(minutes / 60);
  if (hours < 24) {return `${hours}h ago`;}
  const days = Math.round(hours / 24);
  if (days < 7) {return `${days}d ago`;}
  return new Date(iso).toLocaleDateString();
}

export function InboxPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = (searchParams.get("filter") as Filter | null) ?? "all";
  const offset = Number.parseInt(searchParams.get("offset") ?? "0", 10) || 0;

  const [items, setItems] = useState<NotificationRead[]>([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);

  const updateFilters = useCallback(
    (next: { filter?: string; offset?: number }): void => {
      setSearchParams(
        (cur) => {
          const p = new URLSearchParams(cur);
          for (const [k, v] of Object.entries(next)) {
            if (v === undefined || v === "" || v === "all" || v === 0) {
              p.delete(k);
            } else {
              p.set(k, String(v));
            }
          }
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const fetchPage = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) {return;}
      setLoading(true);
      setError(null);
      try {
        const res = await inboxApi.list(
          accessToken,
          { limit: PAGE_SIZE, offset, only_unread: filter === "unread" },
          signal,
        );
        setItems(res.items);
        setTotal(res.total);
        setUnreadCount(res.unread_count);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        setError(
          e instanceof ApiError ? e.problem.detail : "Couldn't load inbox.",
        );
      } finally {
        setLoading(false);
      }
    },
    [accessToken, filter, offset],
  );

  useEffect(() => {
    const c = new AbortController();
    void fetchPage(c.signal);
    return () => { c.abort(); };
  }, [fetchPage]);

  const onRowClick = async (n: NotificationRead): Promise<void> => {
    if (n.read_at === null) {
      setItems((cur) =>
        cur.map((row) =>
          row.id === n.id ? { ...row, read_at: new Date().toISOString() } : row,
        ),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
      if (accessToken !== null) {
        inboxApi.markOneRead(accessToken, n.id).catch(() => {
          // swallow — next refresh reconciles
        });
      }
    }
    const route = n.data["route_path"];
    if (typeof route === "string" && route.length > 0) {
      navigate(route);
    }
  };

  const onMarkAllRead = async (): Promise<void> => {
    if (accessToken === null) {return;}
    setMarkingAll(true);
    try {
      const res = await inboxApi.markAllRead(accessToken);
      toast.success(
        res.marked_read === 0
          ? "Inbox already up to date."
          : `Marked ${res.marked_read} as read.`,
      );
      await fetchPage();
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.problem.detail : "Couldn't mark all.";
      toast.error(msg);
    } finally {
      setMarkingAll(false);
    }
  };

  const canPrev = offset > 0;
  const canNext = offset + items.length < total;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Inbox"
        description="Every notification this account has received. Bell-icon feed parity with the mobile app."
        actions={
          <Button
            variant="outline"
            onClick={() => void onMarkAllRead()}
            disabled={markingAll || unreadCount === 0}
          >
            <CheckCheck className="mr-1 h-4 w-4" />
            {markingAll ? "Marking…" : `Mark all read (${unreadCount})`}
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" /> Notifications
          </CardTitle>
          <CardDescription>
            {total > 0
              ? `Showing ${offset + 1}–${offset + items.length} of ${total}. ${unreadCount} unread.`
              : "No notifications yet."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex gap-1">
            {(["all", "unread"] as Filter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => { updateFilters({ filter: f, offset: 0 }); }}
                className={cn(
                  "rounded px-3 py-1 text-sm capitalize",
                  filter === f
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                {f}
                {f === "unread" && unreadCount > 0 && (
                  <span className="ml-1 text-xs">({unreadCount})</span>
                )}
              </button>
            ))}
          </div>

          {error !== null && (
            <Alert variant="destructive" className="mb-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading && items.length === 0 ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">
              {filter === "unread"
                ? "Nothing unread. You're all caught up."
                : "Notifications arrive here whenever the platform sends you a push."}
            </div>
          ) : (
            <ul className="divide-y">
              {items.map((n) => {
                const unread = n.read_at === null;
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => void onRowClick(n)}
                      className={cn(
                        "w-full text-left flex items-start gap-3 px-2 py-3 hover:bg-muted/40 transition-colors",
                        // /25 not /40: the row's preview line is
                        // text-muted-foreground, which only clears 4.5:1 over
                        // a very light tint. Measured 4.54:1 at /25 vs 4.42:1
                        // (fails AA) at /40. The tint is a redundant cue --
                        // unread is also carried by the bold title + Mail icon.
                        unread && "bg-primary-subtle/25",
                      )}
                    >
                      <div className="mt-0.5 shrink-0">
                        {unread ? (
                          <Mail className="h-4 w-4 text-primary" />
                        ) : (
                          <MailOpen className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <span
                            className={cn(
                              "text-sm truncate",
                              unread ? "font-semibold" : "font-medium",
                            )}
                          >
                            {n.title}
                          </span>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatRelative(n.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {n.body}
                        </p>
                        {n.event_type !== null && (
                          <span className="mt-1 inline-block rounded-full bg-muted px-2 py-0.5 text-xs font-mono">
                            {n.event_type}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="mt-4 flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              disabled={!canPrev || loading}
              onClick={() => { updateFilters({ offset: Math.max(0, offset - PAGE_SIZE) }); }}
            >
              <ChevronLeft className="mr-1 h-4 w-4" /> Previous
            </Button>
            <span className="text-xs text-muted-foreground hidden sm:inline">
              {loading ? "Loading…" : `Page ${Math.floor(offset / PAGE_SIZE) + 1}`}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={!canNext || loading}
              onClick={() => { updateFilters({ offset: offset + PAGE_SIZE }); }}
            >
              Next <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
